<h2 mat-dialog-title class="dialog-header">
    <mat-icon>photo_camera</mat-icon>
    <span>Update Profile Photo</span>
    <button mat-icon-button mat-dialog-close class="close-button">
        <mat-icon>close</mat-icon>
    </button>
</h2>

<mat-dialog-content class="dialog-content">
    <!-- Loading spinner -->
    <div class="loading-overlay" *ngIf="isLoading">
        <mat-spinner diameter="40"></mat-spinner>
        <span>Processing...</span>
    </div>

    <!-- Upload area (shown when no image is loaded) -->
    <div class="upload-area" 
         *ngIf="!imageSrc"
         [class.dragging]="isDragging"
         (dragover)="onDragOver($event)"
         (dragleave)="onDragLeave($event)"
         (drop)="onDrop($event)"
         (click)="triggerFileInput()">
        
        <mat-icon class="upload-icon">cloud_upload</mat-icon>
        <h3>Drag & drop an image here</h3>
        <p>or click to browse</p>
        <p class="hint">Supports: JPG, PNG, GIF (Max 5MB)</p>
        
        <input #fileInput 
               type="file" 
               accept="image/*" 
               (change)="onFileSelected($event)"
               hidden>
    </div>

    <!-- Color Picker Section (shown when no image is loaded) -->
    <div class="color-picker-section" *ngIf="!imageSrc">
        <div class="color-picker-header">
            <mat-icon>palette</mat-icon>
            <span>Or choose a placeholder color</span>
        </div>
        <div class="color-picker-preview">
            <div class="color-avatar-preview" [style.background]="selectedColor">
                {{ userInitials }}
            </div>
        </div>
        <div class="color-palette">
            <button *ngFor="let color of colorPalette"
                    class="color-swatch"
                    [style.background]="color"
                    [class.selected]="color === selectedColor && !isCustomColor"
                    (click)="selectColor(color); $event.stopPropagation()"
                    [matTooltip]="color">
                <mat-icon *ngIf="color === selectedColor && !isCustomColor" class="check-icon">check</mat-icon>
            </button>
            <!-- User-added custom colors -->
            <button *ngFor="let color of customColors"
                    class="color-swatch custom-added-swatch"
                    [style.background]="color"
                    [class.selected]="color === selectedColor && !isCustomColor"
                    (click)="selectColor(color); $event.stopPropagation()"
                    [matTooltip]="color">
                <mat-icon *ngIf="color === selectedColor && !isCustomColor" class="check-icon">check</mat-icon>
            </button>
            <!-- Custom color picker (dotted circle with ngx-color-picker) -->
            <div class="color-swatch custom-color-swatch"
                    [class.active-preview]="isCustomColor"
                    [style.background]="isCustomColor ? selectedColor : null"
                    matTooltip="Pick a custom color"
                    [(colorPicker)]="cpColor"
                    [cpUseRootViewContainer]="true"
                    [cpPosition]="'bottom'"
                    [cpPositionOffset]="'10'"
                    [cpOKButton]="true"
                    [cpOKButtonText]="'Done'"
                    [cpCancelButton]="true"
                    [cpCancelButtonText]="'Cancel'"
                    [cpSaveClickOutside]="false"
                    [cpCloseClickOutside]="true"
                    [cpOutputFormat]="'hex'"
                    (colorPickerChange)="onCpColorChange($event)"
                    (colorPickerOpen)="onCpOpen()"
                    (colorPickerClose)="onCpClose()"
                    (colorPickerSelect)="onCpSelect($event)"
                    (colorPickerCancel)="onCpCancel()">
                <mat-icon *ngIf="isCustomColor" class="check-icon">check</mat-icon>
                <mat-icon *ngIf="!isCustomColor" class="add-icon">colorize</mat-icon>
            </div>
        </div>
    </div>

    <!-- Cropper area (shown when image is loaded) -->
    <div class="cropper-container" *ngIf="imageSrc">
        <img #imageElement [src]="imageSrc" alt="Image to crop">
    </div>

    <!-- Cropper controls -->
    <div class="cropper-controls" *ngIf="imageSrc && imageLoaded">
        <div class="control-group">
            <button mat-icon-button (click)="zoomOut()" matTooltip="Zoom Out">
                <mat-icon>zoom_out</mat-icon>
            </button>
            <button mat-icon-button (click)="zoomIn()" matTooltip="Zoom In">
                <mat-icon>zoom_in</mat-icon>
            </button>
        </div>

        <div class="control-group">
            <button mat-icon-button (click)="rotateLeft()" matTooltip="Rotate Left">
                <mat-icon>rotate_left</mat-icon>
            </button>
            <button mat-icon-button (click)="rotateRight()" matTooltip="Rotate Right">
                <mat-icon>rotate_right</mat-icon>
            </button>
        </div>

        <div class="control-group">
            <button mat-icon-button (click)="flipHorizontal()" matTooltip="Flip Horizontal">
                <mat-icon>flip</mat-icon>
            </button>
            <button mat-icon-button (click)="flipVertical()" matTooltip="Flip Vertical">
                <mat-icon style="transform: rotate(90deg);">flip</mat-icon>
            </button>
        </div>

        <div class="control-group">
            <button mat-icon-button (click)="reset()" matTooltip="Reset">
                <mat-icon>refresh</mat-icon>
            </button>
            <button mat-icon-button (click)="clearImage()" matTooltip="Choose Different Image">
                <mat-icon>delete</mat-icon>
            </button>
        </div>
    </div>
</mat-dialog-content>

<mat-dialog-actions align="end">
    <button mat-button 
            *ngIf="hasExistingPhoto" 
            color="warn" 
            (click)="removePhoto()">
        <mat-icon>delete_forever</mat-icon>
        Remove Photo
    </button>
    
    <span class="flex-spacer"></span>
    
    <button mat-button (click)="cancel()">Cancel</button>
    
    <!-- Save Color button (when no image loaded and color changed) -->
    <button mat-raised-button 
            *ngIf="!imageSrc"
            color="accent"
            [disabled]="isLoading || selectedColor === data.currentProfileColor"
            (click)="saveColor()">
        <mat-icon>palette</mat-icon>
        Save Color
    </button>

    <button mat-raised-button 
            color="primary" 
            [disabled]="!imageLoaded || isLoading"
            (click)="save()">
        <mat-icon>check</mat-icon>
        Save Photo
    </button>
</mat-dialog-actions>
